import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE || 'https://dghrik8evf.execute-api.ap-east-1.amazonaws.com'

const UPLOAD_MIME_TYPES = {
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  md: 'text/markdown'
}

function resolveUploadContentType(file) {
  if (file?.type) return file.type
  const ext = file?.name?.split('.').pop()?.toLowerCase()
  return UPLOAD_MIME_TYPES[ext] || 'application/octet-stream'
}

export const ALLOWED_UPLOAD_EXTENSIONS = ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'txt', 'md']
export const ALLOWED_POSTER_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff']
export const MAX_UPLOAD_FILE_SIZE = 50 * 1024 * 1024
export const MAX_POSTER_FILE_SIZE = 10 * 1024 * 1024

const POSTER_MIME_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff'
}

function resolvePosterContentType(file) {
  if (file?.type) return file.type
  const ext = file?.name?.split('.').pop()?.toLowerCase()
  return POSTER_MIME_TYPES[ext] || 'application/octet-stream'
}

const SPEAKER_PASSWORD_STORAGE_KEY = 'speaker_upload_password'

export function getStoredSpeakerPassword() {
  return localStorage.getItem(SPEAKER_PASSWORD_STORAGE_KEY)
}

export function setStoredSpeakerPassword(password) {
  localStorage.setItem(SPEAKER_PASSWORD_STORAGE_KEY, password)
}

export function clearStoredSpeakerPassword() {
  localStorage.removeItem(SPEAKER_PASSWORD_STORAGE_KEY)
}

function createSpeakerClient() {
  return axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' }
  })
}

function speakerAuthHeaders(password = null) {
  const p = password ?? getStoredSpeakerPassword()
  return p ? { 'X-Speaker-Password': p } : {}
}

/** Shared presigned S3 upload (fetch PUT — avoids axios Authorization on S3). */
export async function uploadViaPresignedUrl(file, { getPresignedUrl, confirmUpload }) {
  const contentType = resolveUploadContentType(file)

  const { data: { data: presignedData } } = await getPresignedUrl(
    file.name,
    contentType,
    file.size
  )

  const storedFilename = presignedData.storedFilename || file.name
  const uploadContentType = presignedData.contentType || contentType

  const uploadResponse = await fetch(presignedData.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': uploadContentType }
  })
  if (!uploadResponse.ok) {
    throw new Error(`Request failed with status code ${uploadResponse.status}`)
  }

  const confirmResponse = await confirmUpload(
    presignedData.fileKey,
    storedFilename,
    file.size
  )
  const confirmData = confirmResponse?.data?.data ?? confirmResponse

  return {
    key: presignedData.fileKey,
    filename: storedFilename,
    originalFilename: file.name,
    size: file.size,
    linked: confirmData?.linked ?? Boolean(confirmData?.entryId)
  }
}

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to all requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      // Use href with hash for HashRouter
      if (!window.location.hash.includes('/login')) {
        window.location.href = '/#/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  logout: () =>
    api.post('/auth/logout'),

  me: () =>
    api.get('/auth/me')
}

// Users API (SysAdmin)
export const usersApi = {
  list: () =>
    api.get('/users'),

  create: (data) =>
    api.post('/users', data),

  update: (userId, data) =>
    api.put(`/users/${userId}`, data),

  delete: (userId) =>
    api.delete(`/users/${userId}`),

  /** Re-check current sysadmin password before unlocking password field */
  verifySysadminPassword: (password) =>
    api.post('/users/verify-password', { password })
}

// Entries API
export const entriesApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/entries${query ? `?${query}` : ''}`)
  },

  create: (data) =>
    api.post('/entries', data),

  update: (entryId, data) =>
    api.put(`/entries/${entryId}`, data),

  lock: (entryId, force = false) =>
    api.post(`/entries/${entryId}/lock`, { force }),

  unlock: (entryId) =>
    api.delete(`/entries/${entryId}/lock`),

  delete: (entryId) =>
    api.delete(`/entries/${entryId}`)
}

// Files API
export const filesApi = {
  // List files with original filenames (supports Chinese characters)
  listFiles: (prefix = '') =>
    api.get(`/files/list${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`),

  getPresignedUrl: (filename, contentType, fileSize, { church, eventDate, speaker } = {}) =>
    api.post('/files/presigned-url', { filename, contentType, fileSize, church, eventDate, speaker }),

  confirmUpload: (fileKey, filename, fileSize, entryId = null) =>
    api.post('/files/confirm', { fileKey, filename, fileSize, entryId }),

  getDownloadUrl: (fileKey) =>
    api.get('/files/download', { params: { key: fileKey } }),

  // Upload file using presigned URL (with original filename metadata)
  uploadWithPresignedUrl: async (file, { church, eventDate, speaker } = {}) =>
    uploadViaPresignedUrl(file, {
      getPresignedUrl: (filename, contentType, fileSize) =>
        filesApi.getPresignedUrl(filename, contentType, fileSize, { church, eventDate, speaker }),
      confirmUpload: (fileKey, filename, fileSize) =>
        filesApi.confirmUpload(fileKey, filename, fileSize)
    }),

  getPosterPresignedUrl: (filename, contentType, fileSize, { church, eventDate } = {}) =>
    api.post('/files/poster/presigned-url', { filename, contentType, fileSize, church, eventDate }),

  confirmPosterUpload: (originalKey, entryId = null) =>
    api.post('/files/poster/confirm', { originalKey, fileKey: originalKey, entryId }),

  getPosterStatus: (posterKey) =>
    api.get('/files/poster/status', { params: { key: posterKey } }),

  getPosterDownloadUrl: (posterKey) =>
    api.get('/files/poster/download', { params: { key: posterKey } }),

  /** Upload poster original, confirm, poll until 1280×720 JPEG is ready. */
  uploadPosterWithPresignedUrl: async (file, { church, eventDate, entryId } = {}) => {
    const contentType = resolvePosterContentType(file)
    const { data: { data: presigned } } = await filesApi.getPosterPresignedUrl(
      file.name,
      contentType,
      file.size,
      { church, eventDate }
    )

    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': presigned.contentType || contentType }
    })
    if (!uploadResponse.ok) {
      throw new Error(`Poster upload failed with status ${uploadResponse.status}`)
    }

    const { data: { data: confirmed } } = await filesApi.confirmPosterUpload(
      presigned.originalKey || presigned.fileKey,
      entryId
    )

    const posterKey = confirmed.posterKey || presigned.posterKey
    let ready = confirmed.ready
    for (let i = 0; i < 20 && !ready; i++) {
      await new Promise(r => setTimeout(r, 500))
      const { data: { data: status } } = await filesApi.getPosterStatus(posterKey)
      ready = status.ready
    }

    return { posterKey, ready, originalKey: confirmed.originalKey || presigned.originalKey }
  }
}

// 家事報告 (ministry) API — same auth as main api client
export const ministryApi = {
  getPresignedUrl: (filename, contentType, fileSize, { church, eventDate } = {}) =>
    api.post('/files/ministry/presigned-url', { filename, contentType, fileSize, church, eventDate }),

  confirmUpload: (originalKey) =>
    api.post('/files/ministry/confirm', { originalKey }),

  list: (church, date, withUrls = false) =>
    api.get(`/files/ministry/list?church=${church}&date=${date}${withUrls ? '&urls=1' : ''}`),

  deleteImage: (key) =>
    api.delete(`/files/ministry/image?key=${encodeURIComponent(key)}`),

  applyOrder: (church, eventDate, keys) =>
    api.post('/files/ministry/order', { church, eventDate, keys }),

  /** Upload one image: presign → PUT → confirm (compress + rename to NNN). */
  uploadImage: async (file, { church, eventDate } = {}) => {
    const contentType = resolvePosterContentType(file)
    const { data: { data: presigned } } = await ministryApi.getPresignedUrl(
      file.name,
      contentType,
      file.size,
      { church, eventDate }
    )

    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': presigned.contentType || contentType }
    })
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status ${uploadResponse.status}`)
    }

    const { data: { data: confirmed } } = await ministryApi.confirmUpload(presigned.originalKey)
    return confirmed
  }
}

// Speaker upload API (/upload page — public form, password required to upload)
export const speakerApi = {
  lookupEntry: (church, date, type) =>
    axios.get(`${API_BASE}/speaker/entry`, { params: { church, date, type } }),

  getPresignedUrl: (filename, contentType, fileSize, { church, eventDate, speaker, password } = {}) =>
    createSpeakerClient().post('/speaker/presigned-url', {
      filename,
      contentType,
      fileSize,
      church,
      eventDate,
      speaker
    }, { headers: speakerAuthHeaders(password) }),

  confirmUpload: (fileKey, filename, fileSize, { church, eventDate, type, password } = {}) =>
    createSpeakerClient().post('/speaker/confirm', {
      fileKey,
      filename,
      fileSize,
      church,
      eventDate,
      type
    }, { headers: speakerAuthHeaders(password) }),

  uploadWithPresignedUrl: async (file, { church, eventDate, speaker, type, password } = {}) =>
    uploadViaPresignedUrl(file, {
      getPresignedUrl: (filename, contentType, fileSize) =>
        speakerApi.getPresignedUrl(filename, contentType, fileSize, { church, eventDate, speaker, password }),
      confirmUpload: (fileKey, filename, fileSize) =>
        speakerApi.confirmUpload(fileKey, filename, fileSize, { church, eventDate, type, password })
    })
}

// Admin API
export const adminApi = {
  getOverview: (view = 'default') =>
    api.get(`/admin/overview?view=${view}`),

  getEntriesByDate: (date) =>
    api.get(`/admin/entries/${date}`),

  getLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/admin/logs${query ? `?${query}` : ''}`)
  },

  getCopyText: (date) =>
    api.get(`/admin/copy/${date}`),

  getYoutubeStatus: (date) =>
    api.get(`/admin/youtube/${date}`),

  refreshYoutube: (date, church = null) =>
    api.post(`/admin/youtube/${date}/refresh`, church ? { church } : {}),

  getMaintenance: () =>
    api.get('/admin/config/maintenance'),

  setMaintenance: (enabled, message = '') =>
    api.post('/admin/config/maintenance', { enabled, message }),

  getNotes: (category = null) => {
    const params = category ? `?category=${category}` : ''
    return api.get(`/admin/notes${params}`)
  },

  createNote: (title, description = '', category = 'feature') =>
    api.post('/admin/notes', { title, description, category }),

  updateNote: (noteId, data) =>
    api.put(`/admin/notes/${noteId}`, data),

  deleteNote: (noteId) =>
    api.delete(`/admin/notes/${noteId}`),

  // Documents API (SysAdmin)
  getDocuments: () =>
    api.get('/admin/documents'),

  getDocumentUploadUrl: (filename) =>
    api.post('/admin/documents/upload', { filename, contentType: 'text/markdown' }),

  getDocumentDownloadUrl: (key) =>
    api.get(`/admin/documents/download?key=${encodeURIComponent(key)}`),

  deleteDocument: (key) =>
    api.delete(`/admin/documents/${encodeURIComponent(key)}`)
}

// Public API (no authentication required)
export const publicApi = {
  getEvents: () =>
    axios.get(`${API_BASE}/public/events`)
}

export default api